import React from "react";
import {
  Box,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";

// Icons
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

// Spinner
import Spinner from "../spinner/Spinner";
import { TablePaginationActionsProps } from "@mui/material/TablePagination/TablePaginationActions";

const CustomDataTable = ({
  sx,
  columns,
  isLoading = false,
  rowsContent,
  rowsPerPageOptions = [10, 20, 50],
  rowsPerPage = 10,
  page = 0,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
  Actions = false,
  pagination = false,
  bodySx,
  sxHeader,
}: {
  sx?: SxProps<Theme>;
  sxHeader?: SxProps<Theme>;
  bodySx?: SxProps<Theme>;
  columns: React.ReactNode[];
  isLoading?: boolean;
  rowsContent: React.ReactNode[] | React.ReactNode;
  rowsPerPageOptions?: number[];
  rowsPerPage?: number;
  page?: number;
  totalCount?: number;
  onPageChange?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  Actions?: boolean;
  pagination?: boolean;
}) => {
  // Custom Pagination Actions
  // const TablePaginationActions = (props: TablePaginationActionsProps) => {
  //   const theme = useTheme();
  //   const { count, page, rowsPerPage, onPageChange } = props;

  //   const handleFirstPageButtonClick = (
  //     event: React.MouseEvent<HTMLButtonElement>
  //   ) => {
  //     onPageChange(event, 0);
  //   };

  //   const handleBackButtonClick = (
  //     event: React.MouseEvent<HTMLButtonElement>
  //   ) => {
  //     onPageChange(event, page - 1);
  //   };

  //   const handleNextButtonClick = (
  //     event: React.MouseEvent<HTMLButtonElement>
  //   ) => {
  //     onPageChange(event, page + 1);
  //   };

  //   const handleLastPageButtonClick = (
  //     event: React.MouseEvent<HTMLButtonElement>
  //   ) => {
  //     onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  //   };

  //   return (
  //     <Box
  //       sx={{
  //         flexShrink: 0,
  //         display: "flex",
  //         alignItems: "center",
  //         order: 99, // <-- FORCE it to the right
  //       }}
  //     >
  //       <IconButton
  //         onClick={handleFirstPageButtonClick}
  //         disabled={page === 0}
  //         aria-label="first page"
  //         sx={{ marginLeft: "10px" }}
  //       >
  //         {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
  //       </IconButton>
  //       <IconButton
  //         onClick={handleBackButtonClick}
  //         disabled={page === 0}
  //         aria-label="previous page"
  //       >
  //         {theme.direction === "rtl" ? (
  //           <KeyboardArrowRight />
  //         ) : (
  //           <KeyboardArrowLeft />
  //         )}
  //       </IconButton>
  //       <IconButton
  //         onClick={handleNextButtonClick}
  //         disabled={page >= Math.ceil(count / rowsPerPage) - 1}
  //         aria-label="next page"
  //       >
  //         {theme.direction === "rtl" ? (
  //           <KeyboardArrowLeft />
  //         ) : (
  //           <KeyboardArrowRight />
  //         )}
  //       </IconButton>
  //       <IconButton
  //         onClick={handleLastPageButtonClick}
  //         disabled={page >= Math.ceil(count / rowsPerPage) - 1}
  //         aria-label="last page"
  //         sx={{ marginRight: "10px" }}
  //       >
  //         {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
  //       </IconButton>
  //     </Box>
  //   );
  // };

  const TablePaginationActions = (props: TablePaginationActionsProps) => {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const totalPages = Math.ceil(count / rowsPerPage);

    const handleFirstPageButtonClick = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      onPageChange(event, 0);
    };

    const handleBackButtonClick = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      onPageChange(event, totalPages - 1);
    };

    return (
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          order: 99,
          gap: "2x",
        }}
      >
        {/* FIRST */}
        <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
          {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>

        {/* PREVIOUS */}
        <IconButton onClick={handleBackButtonClick} disabled={page === 0}>
          {theme.direction === "rtl" ? (
            <KeyboardArrowRight />
          ) : (
            <KeyboardArrowLeft />
          )}
        </IconButton>

        {/* PAGE X of Y */}
        <Box>
          <Typography
            variant="tableBody"
            fontSize={"12px"}
            fontWeight={600}
            sx={{ px: 1 }}
          >
            {page + 1} of {totalPages}
          </Typography>
        </Box>

        {/* NEXT */}
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= totalPages - 1}
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowLeft />
          ) : (
            <KeyboardArrowRight />
          )}
        </IconButton>

        {/* LAST */}
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= totalPages - 1}
        >
          {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </Box>
    );
  };

  const TablePaginationActionsNotShow: React.FC = () => {
    return <div />;
  };

  return (
    <TableContainer sx={sx}>
      <Table>
        <TableHead
          className="custom_table_thead"
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: "#F2F3F5 !important",
            zIndex: 10,
            borderBottom: "1px solid #DCDCDC",
            borderTop: "1px solid #DCDCDC",
          }}
        >
          <TableRow>
            {columns?.map((column, index) => (
              <TableCell key={index} className="custom_table_thead_cell">
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={bodySx}>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns?.length} sx={{ textAlign: "center" }}>
                <Spinner />
              </TableCell>
            </TableRow>
          ) : rowsContent &&
            Array.isArray(rowsContent) &&
            rowsContent.length > 0 ? (
            rowsContent
          ) : (
            <TableRow>
              <TableCell colSpan={columns?.length} sx={{ textAlign: "center" }}>
                <Typography variant="body1" fontSize={"15px"} fontWeight={600}>
                  No Data Found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {pagination && (
          <TableFooter
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 10,
              backgroundColor: "#fff",
            }}
          >
            <TableRow>
              <TableCell
                colSpan={columns?.length}
                sx={{ padding: 0, borderBottom: 0 }}
              >
                <Box
                  sx={{
                    padding: "0px 10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#666",
                    }}
                  >
                    Showing {totalCount === 0 ? 0 : page * rowsPerPage + 1} to{" "}
                    {Math.min((page + 1) * rowsPerPage, totalCount)} of{" "}
                    {totalCount} entries
                  </Typography>
                  <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component={"div"}
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, num) => onPageChange?.(e, num)}
                    onRowsPerPageChange={onRowsPerPageChange}
                    ActionsComponent={
                      Actions
                        ? TablePaginationActions
                        : TablePaginationActionsNotShow
                    }
                    // sx={{
                    //   "& .MuiTablePagination-toolbar": {
                    //     display: "flex",
                    //     justifyContent: "space-between",
                    //     alignItems: "center",
                    //     px: 0,
                    //     py: 0,
                    //   },
                    //   // Remove default spacer to allow custom ordering to work reliably
                    //   "& .MuiTablePagination-spacer": {
                    //     flex: "0 0 0 !important",
                    //     width: "0 !important",
                    //   },
                    //   // Left cluster: label + select
                    //   "& .MuiTablePagination-selectLabel": {
                    //     order: 1,
                    //     fontSize: "12px",
                    //     fontWeight: 700,
                    //     marginRight: "8px",
                    //   },
                    //   "& .MuiTablePagination-select": {
                    //     order: 2,
                    //     fontSize: "12px",
                    //     fontWeight: 600,
                    //     minWidth: 64,
                    //   },
                    //   // Right cluster: displayed rows + actions
                    //   "& .MuiTablePagination-displayedRows": {
                    //     order: 3,
                    //     marginLeft: "auto !important",
                    //     fontSize: "12px",
                    //   },
                    //   "& .MuiTablePagination-actions": {
                    //     order: 4,
                    //     marginLeft: "8px",
                    //   },
                    //   borderBottom: "none !important",
                    // }}

                    sx={{
                      "& .MuiTablePagination-toolbar": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 0,
                        py: 0,
                      },

                      // Remove default spacer
                      "& .MuiTablePagination-spacer": {
                        flex: "0 0 0 !important",
                        width: "0 !important",
                      },

                      // LEFT SIDE: label + select
                      "& .MuiTablePagination-selectLabel": {
                        order: 1,
                        fontSize: "12px",
                        fontWeight: 700,
                        mr: 1,
                      },
                      "& .MuiTablePagination-select": {
                        order: 2,
                        fontSize: "12px",
                        fontWeight: 600,
                        minWidth: 64,
                      },

                      // RIGHT SIDE begins
                      "& .MuiTablePagination-displayedRows": {
                        order: 98, // must come before actions
                        ml: "auto",
                        fontSize: "12px",
                        opacity: Actions ? 0 : 1,
                      },

                      "& .MuiTablePagination-actions": {
                        order: 99,
                        ml: 1,
                        display: "flex",
                        alignItems: "center",
                      },

                      borderBottom: "none",
                    }}
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};

export default CustomDataTable;
